"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function () {
  const steps = [{
    number: "01",
    title: "THE QUOTE",
    description: "Send us a photo or schedule a site visit. We provide a transparent, heavy-duty estimate based on volume and labor—no hidden fees, just honest pricing.",
    duration: "SAME DAY"
  }, {
    number: "02",
    title: "SCHEDULING",
    description: "We lock in a window that fits your timeline. Our team coordinates the logistics to ensure a rapid response that respects your property and schedule.",
    duration: "24 HOURS"
  }, {
    number: "03",
    title: "THE HAUL",
    description: "Joe & Gio arrive with the heavy equipment. We handle all the lifting, loading, and sorting, clearing your space with industrial-grade efficiency.",
    duration: "QUICK AND GREAT JOB"
  }, {
    number: "04",
    title: "SORTING",
    description: "We don't just dump. Every load is audited for recyclables and donation-worthy items, minimizing landfill impact and supporting the community.",
    duration: "ON-SITE"
  }, {
    number: "05",
    title: "DISPOSAL",
    highlight: true,
    description: "The final sweep. We transport the remaining debris to certified dump locations, ensuring every piece of junk is disposed of according to local regulations.",
    duration: "IMMEDIATE"
  }, {
    number: "06",
    title: "CLEAN SWEEP",
    description: "A lifetime commitment to your space. We finish with a thorough sweep of the area, leaving your property in better condition than we found it.",
    duration: "COMPLETE"
  }];
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="3595" id="the-haul-it-all-process" className="dark bg-background text-foreground py-24 md:py-32 px-6 md:px-12 lg:px-20">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-24 md:mb-32 transition-all duration-1000 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-7">
            <p className="font-mono text-xs md:text-sm text-primary mb-8 tracking-widest uppercase">
              [ 03 // THE METHOD ]
            </p>
            <h2 className="font-default font-bold text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tighter uppercase text-foreground">
              The Haul It All
              <br />
              <span className="text-foreground/40">/ Process</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-6 flex items-start">
            <p className="font-default text-xl md:text-2xl leading-relaxed text-muted-foreground">
              Six industrial-grade steps from initial quote to final sweep—executed with the precision of a logistics operation.
            </p>
          </div>
        </div>

        {}
        <div className="relative">
          {}
          <div className="absolute top-3 bottom-3 w-px bg-border left-6 md:left-[33.333%]" aria-hidden="true" />

          <div className="space-y-24 md:space-y-32">
            {steps.map((step, i) => {
            return <TimelineStep key={step.number} step={step} i={i} />;
          })}
          </div>
        </div>

        {}
        <div className="mt-32 md:mt-40 text-center">
          <p className="font-default italic text-xl md:text-2xl text-muted-foreground mb-10 max-w-2xl mx-auto">
            Every removal is treated as a high-performance mission.
          </p>
          <Link to="/HomePage" className="inline-block">
            <Button variant="outline" className="font-mono text-xs tracking-widest uppercase border-primary text-primary bg-transparent hover:bg-primary hover:text-primary-foreground h-14 px-10 rounded-md shadow-md transition-all duration-300">
              Start Your Haul
              <ArrowRight className="ml-2 size-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}
function TimelineStep({
  step,
  i
}: {
  step: {
    number: string;
    title: string;
    description: string;
    duration: string;
    highlight?: boolean;
  };
  i: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <div ref={ref} data-index={i} className={`relative grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${i * 80}ms`
  }}>
      {}
      <div className="md:col-span-4 relative flex items-start gap-6 md:block">
        {}
        <div className="absolute md:absolute left-0 md:left-auto md:right-[-13px] top-2 size-6 flex items-center justify-center z-10" aria-hidden="true">
          <div className={`size-6 border-2 ${step.highlight ? "bg-primary border-primary shadow-lg" : "bg-background border-foreground"} rounded-md`} />
        </div>

        {}
        <p className="font-mono text-sm md:text-base text-primary tracking-widest pl-12 md:pl-0 md:pr-12 md:text-right">
          [ {step.number} ]
        </p>
      </div>

      {}
      <div className="md:col-span-8 pl-12 md:pl-8">
        <h3 className="font-default font-bold text-3xl md:text-5xl tracking-tighter uppercase text-foreground mb-5 leading-none">
          {step.title}
        </h3>
        <p className="font-default text-lg md:text-xl leading-relaxed text-muted-foreground mb-6 max-w-2xl">
          {step.description}
        </p>
        <div className="flex items-center gap-4 mb-6">
          <span className="font-mono text-xs tracking-widest text-foreground border border-border px-3 py-1.5 bg-secondary/50">
            [ {step.duration} ]
          </span>
          {step.highlight && <span className="font-mono text-xs tracking-widest text-primary-foreground bg-primary px-3 py-1.5">
              [ HEAVY DUTY ]
            </span>}
        </div>
        <div className="h-1 bg-primary/20 w-full max-w-2xl" />
      </div>
    </div>;
}